import { useEffect } from 'react';

import get from 'lodash/get';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import ModalHandlerComponent from '../Modal/ModalHandlerComponent';
import CheckmarkOrXComponent from '../Table/CheckmarkOrXComponent';
import SendInviteFormComponent from './SendInviteFormComponent';

import {
  filterObjectByKeyValue,
  formatDate,
  isAdmin,
  isPM,
  replaceUnderscoreWithDash,
  toSentenceCase,
} from '../../utils';
import {
  checkHasRole,
  contactPersonRole,
  getFullName,
  getRoleName,
} from '../Person/utils';
import { getAddedByCell } from '../Table/utils';
import { getExportTitle } from '../ViewExport/utils';
import {
  canEditBroker,
  canEditVisitor,
  getPropertyInfo,
  getVisitorRoleFilterOptions,
} from './utils';

import { SYNC_ACCOUNT_TYPE } from '../BuildingSettings/constants';
import { roleNames } from '../Person/constants';
import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import { history } from '../../store';
import AdminAction from '../AdminAction';
import WithLink from '../Linkify';
import { CUSTOM } from '../Modal/modalTypes';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import ListActions from '../Table/ListActions';
import {
  buildingFilter,
  limitFilter,
  roleFilter,
  unitFilter,
  visitorStatusFilter,
} from '../Table/filters';
import Tooltip from '../Tooltip';
import VisitorTableExport from './VisitorTableExport';

const VisitorTableComponent = (props) => {
  const { t } = useTransl();
  const theme = useTheme();
  const {
    items,
    onLoad,
    getItems,
    getUnits,
    listParams,
    listKey,
    building,
    buildings,
    units,
    sendInvite,
    deleteInvite,
    expireVisitor,
    isInviteModalShown,
    onShowInviteModal,
    onCloseInviteModal,
    onSendInvite,
    user,
    inviteModalData,
    syncWithBrivo,
    pmc,
    match: { url, params },
    onExport,
    onLogEntry,
    isVisitorProfile,
    syncPerson,
  } = props;

  const columns = [
    {
      Header: t('title.visitor'),
      id: 'visitor_first_name',
      accessor: ({ id, person }, isExport) => {
        if (isExport) return getFullName(person);

        return (
          <CellPersonWithImage
            person={person}
            linkUrl={`/visitors/profile/${id}`}
          />
        );
      },
      headerClassName: 'sort',
      minWidth: 150,
    },
    {
      Header: t('title.role'),
      id: 'role',
      accessor: ({ role }) => toSentenceCase(getRoleName(role)),
      width: 80,
      sortable: false,
    },
    {
      Header: t('title.property'),
      id: 'building',
      accessor: (entry, isExport) => {
        if (isExport) return entry?.buildingName ?? entry?.pmcName;

        const propertyInfo = getPropertyInfo(entry);
        const { id, name, type } = propertyInfo;

        if (id && name && type) {
          return <StyledLink to={`/${type}/${id}`}>{name}</StyledLink>;
        }

        return '';
      },
      headerClassName: 'sort',
    },
    {
      Header: t('title.unit'),
      id: 'unit',
      accessor: ({ role, building, unit, unitName }, isExport) => {
        const isBroker = checkHasRole(roleNames.broker, role);
        if (isBroker) return '';
        if (building && unit && unitName) {
          return isExport ? (
            unitName
          ) : (
            <StyledLink to={`/building/${building}/units/${unit}`}>
              {unitName}
            </StyledLink>
          );
        }
        return t('roles.buildingVisitor');
      },
      headerClassName: 'sort',
      width: 120,
    },
    {
      Header: t('title.comments'),
      id: 'comments',
      accessor: ({ comments }, isExport) => {
        if (isExport) return comments ?? '';

        return (
          <Tooltip tooltip={comments}>
            <WithLink>{comments}</WithLink>
          </Tooltip>
        );
      },
      sortable: false,
    },
    {
      Header: t('title.addedBy'),
      id: 'creator_first_name',
      accessor: (entry, isExport) => {
        if (isExport) return getFullName(entry?.creator);
        return getAddedByCell(entry, user);
      },
      headerClassName: 'sort',
    },
    {
      Header: t('title.role'),
      id: 'creator_role',
      sortable: false,
      accessor: ({ creator = {} }) => contactPersonRole(creator),
    },
    {
      Header: t('visitor.authOn'),
      id: 'created_at',
      headerClassName: 'sort',
      width: 160,
      accessor: (entry) => formatDate(get(entry, 'createdAt')),
    },
    {
      Header: t('title.lastEntryDate'),
      id: 'latest_visit_time',
      accessor: ({ latestVisitTime }) =>
        latestVisitTime ? formatDate(latestVisitTime) : t('time.never'),
      headerClassName: 'sort',
      width: 160,
    },
    {
      Header: t('title.validFrom'),
      id: 'valid_from',
      accessor: ({ validFrom }) => {
        if (validFrom) {
          return formatDate(validFrom, false);
        }
        return '';
      },
      headerClassName: 'sort',
    },
    {
      Header: t('title.validUntil'),
      id: 'valid_until',
      accessor: ({ validUntil }) => {
        if (validUntil) {
          if (Date.now() > Date.parse(validUntil)) {
            return t('visitor.expired');
          }
          return formatDate(validUntil, false);
        }
        return t('visitor.permanent');
      },
      headerClassName: 'sort',
    },
    {
      Header: t('title.appInstalled'),
      id: 'app_installed',
      headerClassName: 'admin-feature',
      sortable: false,
      show: isAdmin(),
      accessor: ({ appInstalled }, isExport) => {
        if (isExport) return appInstalled ? t('common.yes') : t('common.no');

        return <CheckmarkOrXComponent value={appInstalled} />;
      },
    },
    {
      Header: t('title.digitalKey'),
      id: 'invite_sent',
      headerClassName: 'sort',
      accessor: ({ inviteSent }, isExport) => {
        if (isExport) return inviteSent ? t('common.yes') : t('common.no');

        return <CheckmarkOrXComponent value={inviteSent} />;
      },
    },
    {
      Header: t('syncActions.brivoSync'),
      id: 'synchronized_with_brivo',
      accessor: ({ person }, isExport) => {
        if (isExport)
          return person?.synchronizedWithBrivo
            ? t('common.yes')
            : t('common.no');

        return <CheckmarkOrXComponent value={person?.synchronizedWithBrivo} />;
      },
      headerClassName: 'admin-feature',
      sortable: false,
      show: isAdmin(),
    },
    {
      Header: t('title.actions'),
      id: 'actions',
      fixed: 'right',
      sortable: false,
      width: 120,
      show: !isVisitorProfile,
      accessor: (entry) => {
        const {
          id,
          person,
          building: buildingId,
          role,
          inviteSent,
          validUntil,
        } = entry;
        const personId = person?.id;
        const roleName = getRoleName(role);

        const userBuildings = user?.properties || [];
        const userBuilding = userBuildings.find(({ id }) => id === buildingId);
        const isEnableToSendInvite = userBuilding?.visitorInviteEnabled ?? true;

        const isBroker = checkHasRole(roleNames.broker, role);
        const isBrokerAvailable = isAdmin() || isPM(params);
        const isExpired = Date.now() > Date.parse(validUntil);
        const canExpire = isAdmin() || isPM(params);
        const canEdit = isBroker
          ? canEditBroker(params)
          : canEditVisitor(entry);

        const {
          associatedWithBrivo,
          associatedWithDormakaba,
          associatedWithBmx,
          associatedWithComelit,
          associatedWithSalto,
          associatedWithSmartair,
          associatedWithTwoN,
          associatedWithKeyscan,
          id: syncBuildingId,
        } = building ?? {};

        const {
          synchronizedWithDormakaba,
          synchronizedWithBrivo,
          synchronizedWithBmx,
          synchronizedWithComelit,
          synchronizedWithSalto,
          synchronizedWithSmartair,
          synchronizedWithTwoN,
          synchronizedWithKeyscan,
        } = person ?? {};

        const isBrivoOn =
          Boolean(associatedWithBrivo) && person && !synchronizedWithBrivo;
        const isDormakabaOn =
          Boolean(associatedWithDormakaba) &&
          person &&
          !synchronizedWithDormakaba;
        const isBmxOn =
          Boolean(associatedWithBmx) && person && !synchronizedWithBmx;
        const isComelitOn =
          Boolean(associatedWithComelit) && person && !synchronizedWithComelit;
        const isSaltoOn =
          Boolean(associatedWithSalto) && person && !synchronizedWithSalto;
        const isSmartairOn =
          Boolean(associatedWithSmartair) &&
          person &&
          !synchronizedWithSmartair;
        const isTwoNOn =
          Boolean(associatedWithTwoN) && person && !synchronizedWithTwoN;
        const isKeyscanOn =
          Boolean(associatedWithKeyscan) && person && !synchronizedWithKeyscan;

        const actions = [
          {
            label: canEdit ? t('actions.update') : t('events.viewDetails'),
            url: `/visitors/manage/update-${replaceUnderscoreWithDash(
              roleName,
            )}/${id}`,
          },
          {
            label: `${t('actions.expire')} ${toSentenceCase(roleName)}`,
            color: theme.palette.secondary.main,
            onClick: () => expireVisitor(id, listParams),
            disabled: isExpired,
            show: canExpire && !isExpired,
          },
          {
            label: inviteSent
              ? t('actions.resendInvite')
              : t('actions.sendInvite'),
            disabled: isExpired || !isEnableToSendInvite,
            onClick: () => {
              if (!isEnableToSendInvite) return;
              inviteSent
                ? sendInvite(null, id, inviteSent, listParams)
                : onShowInviteModal(entry, false);
            },
            show: canEdit,
          },
          {
            label: t('actions.updateInvite'),
            disabled: !isEnableToSendInvite || !inviteSent,
            onClick: () =>
              isEnableToSendInvite &&
              inviteSent &&
              onShowInviteModal(entry, true),
            show: canEdit && !isBroker,
          },
          {
            label: t('actions.expireInvite'),
            color: theme.palette.secondary.main,
            onClick: () => deleteInvite(id, listParams),
            show: inviteSent && canEdit && !isBroker,
          },
          {
            label: <AdminAction>{t('syncActions.bmx')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.bmx),
            show: isAdmin() && isBmxOn,
          },
          {
            label: <AdminAction>{t('syncActions.brivo')}</AdminAction>,
            onClick: () => syncWithBrivo(syncBuildingId, personId, listParams),
            show: isAdmin() && isBrivoOn,
          },
          {
            label: <AdminAction>{t('syncActions.comelit')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.comelit),
            show: isAdmin() && isComelitOn,
          },
          {
            label: <AdminAction>{t('syncActions.dormakaba')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.dormakaba),
            show: isAdmin() && isDormakabaOn,
          },
          {
            label: <AdminAction>{t('syncActions.salto')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.salto),
            show: isAdmin() && isSaltoOn,
          },
          {
            label: <AdminAction>{t('syncActions.smartair')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.smartair),
            show: isAdmin() && isSmartairOn,
          },
          {
            label: <AdminAction>{t('syncActions.two_n')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.twoN),
            show: isAdmin() && isTwoNOn,
          },
          {
            label: <AdminAction>{t('syncActions.keyscan')}</AdminAction>,
            onClick: () =>
              syncPerson(syncBuildingId, personId, SYNC_ACCOUNT_TYPE.keyscan),
            show: isAdmin() && isKeyscanOn,
          },
          {
            label: t('title.logEntry'),
            onClick: () => onLogEntry(id),
            show: !isAdmin(),
          },
        ];
        if (isBroker) {
          isBrokerAvailable && !inviteSent && actions.splice(2, 1);
          !isBrokerAvailable && actions.splice(0, 6); // display only 'log entry'
        }
        return <ListActions actions={actions} isFullWidth />;
      },
    },
  ];

  const buildingId = building?.id;
  const buildingName = building?.name;

  const filteredBuildings = pmc?.id
    ? filterObjectByKeyValue(buildings, 'parent.id', pmc.id)
    : buildings;

  const dateRangeTypeOptions = [
    DATE_RANGES_TYPES.created_at,
    DATE_RANGES_TYPES.valid_until,
  ];

  const filters = isVisitorProfile
    ? []
    : [
        buildingFilter(listParams, filteredBuildings),
        visitorStatusFilter(listParams),
        roleFilter(listParams, getVisitorRoleFilterOptions()),
        limitFilter(listParams),
      ];

  const selectedBuildingId = listParams?.building;

  useEffect(() => {
    selectedBuildingId && !isVisitorProfile && getUnits(selectedBuildingId);
  }, [selectedBuildingId]);

  if (selectedBuildingId && !isVisitorProfile) {
    filters.splice(1, 0, unitFilter(listParams, units));
  }

  if (buildingId) {
    filters.shift();
  }

  const isBrokerAvailable = isAdmin() || isPM(params);

  const actions = [
    {
      label: t('title.visitorOrVendor'),
      onClick: () =>
        history.push({
          pathname: `${url}/manage/create-visitor`,
          state: { buildingId, buildingName },
        }),
    },
    {
      label: t('roles.broker'),
      onClick: () =>
        history.push({
          pathname: `${url}/manage/create-broker`,
          state: { buildingId, buildingName },
        }),
      show: isBrokerAvailable,
    },
  ];

  const defaultParams = {
    status: 'active',
    ordering: '-created_at',
    building: params?.buildingId,
    pmc: params?.PMCId,
  };

  const tableDataType = TABLE_DATA_TYPES.visitors;
  const modalProps = {
    modal: {
      modalType: CUSTOM,
      modalProps: {
        title: inviteModalData?.isInviteUpdate
          ? t('actions.updateInvite')
          : t('actions.sendInvite'),
      },
    },
    onModalClose: onCloseInviteModal,
  };

  const title = t('title.visitorsAndBrokers');
  const exportTitle = getExportTitle(title, pmc, building);
  const exportActions = isVisitorProfile ? null : (
    <VisitorTableExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );
  const listActions = isVisitorProfile ? null : (
    <ListActions
      actions={actions}
      title={t('actions.addNew')}
      color={theme.palette.success.main}
    />
  );
  return (
    <>
      <AsyncTableComponent
        tableId={TABLE_ID.visitorsTable}
        title={title}
        action={listActions}
        columns={columns}
        defaultSort={TABLE_CONSTANTS.created_at}
        defaultSortDesc
        manual
        filters={filters}
        showFiltersAreApplied={false}
        items={items}
        onLoad={onLoad}
        getItems={getItems}
        listParams={listParams}
        defaultParams={defaultParams}
        tableDataType={tableDataType}
        listKey={listKey}
        dateRangeType={DATE_RANGES_TYPES.created_at}
        dateRangeTypeOptions={dateRangeTypeOptions}
        datePicker
        shouldUpdate={!isInviteModalShown}
        exportActions={exportActions}
        isHideSearch={isVisitorProfile}
      />
      {isInviteModalShown && (
        <ModalHandlerComponent {...modalProps}>
          <SendInviteFormComponent
            onSubmit={onSendInvite}
            inviteModalData={inviteModalData}
          />
        </ModalHandlerComponent>
      )}
    </>
  );
};
VisitorTableComponent.defaultProps = {
  items: null,
  buildings: null,
  building: null,
  units: null,
  isVisitorProfile: false,
};
VisitorTableComponent.propTypes = {
  match: PropTypes.instanceOf(Object),
  items: PropTypes.instanceOf(Object),
  buildings: PropTypes.instanceOf(Object),
  building: PropTypes.instanceOf(Object),
  pmc: PropTypes.instanceOf(Object),
  units: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object),
  inviteModalData: PropTypes.instanceOf(Object),
  listParams: PropTypes.instanceOf(Object).isRequired,
  isVisitorProfile: PropTypes.bool,
  listKey: PropTypes.string.isRequired,
  isInviteModalShown: PropTypes.bool.isRequired,
  deleteInvite: PropTypes.func.isRequired,
  onSendInvite: PropTypes.func.isRequired,
  syncWithBrivo: PropTypes.func.isRequired,
  sendInvite: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  onShowInviteModal: PropTypes.func.isRequired,
  onCloseInviteModal: PropTypes.func.isRequired,
  onLogEntry: PropTypes.func.isRequired,
  syncPerson: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  expireVisitor: PropTypes.func.isRequired,
  getUnits: PropTypes.func.isRequired,
};

export default VisitorTableComponent;
